import {
  BreakoutsFilledIcon,
  Button,
  ChatIcon,
  NotesIcon,
  PollFilledIcon,
  RefreshIcon,
  ResourcesFilledIcon,
  UserIcon,
  XDeleteIcon,
} from "@minervaproject/fds";
import { ButtonKinds } from "@minervaproject/fds/es/components/button";
import clsx from "clsx";
import { chunk } from "lodash";
import pluralize from "pluralize";
import { ReactElement, useContext, useState } from "react";

import { createPollSessionHelper, endPollSessionHelper } from "api/api_helpers";
import { FlightAPIContext } from "contexts/flight_api_context";
import { ForumAPIContext } from "contexts/forum_api_context";
import { useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import {
  PROFILE_PROPERTIES,
  USER_PICKER_PROPERTIES,
  useUsersProperties,
} from "hooks/use_user_properties";
import {
  endBreakoutSession,
  featureBreakoutRoomResource,
  setAllStagePanes,
} from "shared/reducers/classroom";
import {
  CapabilityType,
  PaneContentType,
  PaneType,
  PollSessionStatusType,
  SidebarPanelType,
  TimelineSegmentOpType,
  TimelineSegmentType,
} from "shared/types";
import { ClassEventTypeType } from "shared/types/class_event";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { classroomSelectors, localSelectors } from "store";
import { setChatTextAreaContent, setSidebarPanel } from "store/reducers/local";
import { createClassEvent, createMonitoringEvent } from "store/reducers/monitoring";
import { isBreakoutBasedOnPoll } from "utils/breakout";
import { featureBreakoutRoom, featurePreclassGroup } from "utils/classroom";
import { EMPTY_PANE } from "utils/stage";
import {
  getBreakoutForTimelineSegment,
  hasPollsInTimelineSegment,
  hasResourcesInTimelineSegment,
} from "utils/timeline";

// Note that some of the ops may contain a `:` followed by some additional data, e.g. `feature-group:2`.
enum TimelineSecondaryActionOpPrefix {
  MessageAll = "message-all",
  EndBreakout = "end-breakout",
  RestartBreakout = "restart-breakout",
  EndPoll = "end-poll",
  RestartPoll = "restart-poll",
  FeatureGroup = "feature-group",
  FeaturePreclassGroup = "feature-preclass-group",
}

type TimelineSecondaryAction = {
  op: string;
  title: string;
  icon: ReactElement;
  disabled?: boolean;
};

function TimelineSegment({
  segment,
  isActive,
  advanceTimeline,
  toggleShowInstructorNotes,
}: {
  segment: TimelineSegmentType;
  isActive: boolean;
  advanceTimeline: (segment: TimelineSegmentType, force?: boolean) => void;
  toggleShowInstructorNotes: () => void;
}) {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const currentlyBackingUpPoll = useAppSelector(localSelectors.selectCurrentlyBackingUpPoll());
  const activeRoomId = useAppSelector(classroomSelectors.selectActiveRoomIdForCurrentUser());
  const users = Object.values(
    useUsersProperties(PROFILE_PROPERTIES.concat(USER_PICKER_PROPERTIES)),
  );
  const preclassGroups = useAppSelector(classroomSelectors.selectPreclassGroups());
  const breakouts = Object.values(useAppSelector(classroomSelectors.selectBreakouts()));
  const hasActiveBreakout = !!useAppSelector(classroomSelectors.selectActiveBreakoutSessionId());
  const breakoutRooms = useAppSelector(classroomSelectors.selectBreakoutRooms());
  const polls = Object.values(useAppSelector(classroomSelectors.selectPolls()));
  const pollSessions = Object.values(useAppSelector(classroomSelectors.selectPollSessions()));
  const pollResponses = Object.values(useAppSelector(classroomSelectors.selectPollResponses()));
  const resources = Object.values(useAppSelector(classroomSelectors.selectResources()));
  const { panes } = useAppSelector(classroomSelectors.selectStage());
  const pollSessionIdsOnStage = useArraySelector(classroomSelectors.selectPollSessionIdsOnStage());
  const pollSessionsOnStage = pollSessions.filter((pollSession) =>
    pollSessionIdsOnStage.includes(pollSession.id),
  );
  const flightAPI = useContext(FlightAPIContext);
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const hasInstructorNotes =
    (segment.notes?.faculty_instructions?.length || 0) > 0 ||
    (segment.notes?.engagement_prompt?.length || 0) > 0 ||
    (segment.notes?.focus_questions?.length || 0) > 0;
  const maxSubscribedStreams = useAppSelector(classroomSelectors.selectMaxSubscribedStreams());
  const isVisibleInClassUserIds = useUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS);
  const presentVisibleUsers = users.filter(
    (user) => user.is_present && isVisibleInClassUserIds.includes(user.id),
  );
  const isAlwaysSubscribedToUserIds = useUserIdsWithCapability(
    CapabilityType.IS_ALWAYS_SUBSCRIBED_TO,
  );
  const canParticipateInClassUserIds = useUserIdsWithCapability(
    CapabilityType.CAN_PARTICIPATE_IN_CLASS,
  );
  const participantBarUserIds = useAppSelector(classroomSelectors.selectParticipantBarUserIds());
  const [isHovered, setIsHovered] = useState(false);

  const getIcon = () => {
    const color = isActive || isHovered ? "white" : undefined;
    if (segment.op === TimelineSegmentOpType.Layout) {
      if (hasPollsInTimelineSegment(segment)) {
        return <PollFilledIcon color={color} />;
      }
      if (hasResourcesInTimelineSegment(segment)) {
        return <ResourcesFilledIcon color={color} />;
      }
    } else if (segment.op === TimelineSegmentOpType.Breakout) {
      return <BreakoutsFilledIcon color={color} />;
    }

    // Displaying a user icon as a default if it doesn't match any of the above.
    return <UserIcon stroke={color} />;
  };

  const performSecondaryAction = async (op: string) => {
    const splitOp = op.split(":");
    const opPrefix = splitOp[0];
    switch (opPrefix) {
      case TimelineSecondaryActionOpPrefix.MessageAll:
        dispatch(setChatTextAreaContent({ text: "@All " }));
        dispatch(setSidebarPanel({ panel: SidebarPanelType.Chat }));
        break;
      case TimelineSecondaryActionOpPrefix.EndBreakout:
        dispatch(endBreakoutSession());
        break;
      case TimelineSecondaryActionOpPrefix.RestartBreakout:
        advanceTimeline(segment, true);
        break;
      case TimelineSecondaryActionOpPrefix.EndPoll:
        pollSessionsOnStage.forEach((pollSession) => {
          if (pollSession.status === PollSessionStatusType.InProgress) {
            const pollSessionResponses = pollResponses.filter(
              (pollResponse) => pollResponse.poll_session_id === pollSession.id,
            );
            const poll = polls.find((_poll) => _poll.id === pollSession.poll_id);
            endPollSessionHelper(
              pollSession,
              pollSessionResponses,
              poll!.type,
              flightAPI.getServerMilliseconds(),
              dispatch,
              forumAPI,
            );
          }
        });
        break;
      case TimelineSecondaryActionOpPrefix.RestartPoll: {
        const newPanes: PaneType[] = [];
        for (const pane of panes) {
          if (pane.content_type === PaneContentType.PollSession) {
            const pollSession = pollSessions.find(
              (_pollSession) => _pollSession.id === pane.content_id,
            );
            if (pollSession && pollSession.status === PollSessionStatusType.InProgress) {
              const pollSessionResponses = pollResponses.filter(
                (pollResponse) => pollResponse.poll_session_id === pollSession.id,
              );
              const poll = polls.find((_poll) => _poll.id === pollSession.poll_id);
              endPollSessionHelper(
                pollSession,
                pollSessionResponses,
                poll!.type,
                flightAPI.getServerMilliseconds(),
                dispatch,
                forumAPI,
              );
            }
            // eslint-disable-next-line no-await-in-loop
            const newPollSession = await createPollSessionHelper(
              pollSession!.poll_id,
              dispatch,
              forumAPI,
            ).catch(() => {
              return null;
            });
            if (newPollSession) {
              newPanes.push({
                content_id: newPollSession.id,
                content_type: PaneContentType.PollSession,
              });
            }
          } else {
            newPanes.push({ ...pane });
          }
        }
        window.flightLogger.log(
          `[TimelineSegment] Updating stage panes: ${JSON.stringify(newPanes)}`,
        );
        dispatch(setAllStagePanes({ panes: newPanes }));
        break;
      }
      case TimelineSecondaryActionOpPrefix.FeatureGroup: {
        const breakoutRoomId = splitOp[1];
        if (isInPerson) {
          dispatch(featureBreakoutRoomResource({ breakoutRoomId }));
        } else {
          const breakoutNotes = resources.filter(
            (resource) =>
              breakoutRooms[breakoutRoomId].resource_ids.includes(resource.id) &&
              resource.is_breakout_group_notes,
          )[0];
          featureBreakoutRoom(
            dispatch,
            breakoutRooms[breakoutRoomId],
            breakoutNotes,
            maxSubscribedStreams,
            currentUserId,
            presentVisibleUsers,
            isAlwaysSubscribedToUserIds,
            canParticipateInClassUserIds,
            participantBarUserIds,
          );
        }
        break;
      }
      case TimelineSecondaryActionOpPrefix.FeaturePreclassGroup: {
        const preclassGroupId = splitOp[1];
        if (isInPerson) {
          window.flightLogger.log(`[TimelineSegment] Updating stage panes: empty 1-up}`);
          dispatch(setAllStagePanes({ panes: [EMPTY_PANE] }));
        } else {
          featurePreclassGroup(
            dispatch,
            preclassGroups[preclassGroupId],
            maxSubscribedStreams,
            currentUserId,
            presentVisibleUsers,
            isAlwaysSubscribedToUserIds,
            canParticipateInClassUserIds,
            participantBarUserIds,
          );
        }
        break;
      }
      default:
        window.flightLogger.log(`Received unknown secondary action op: ${op}`);
    }
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.TIMELINE_SEGMENT_SECONDARY_ACTION,
        userId: currentUserId,
        contextData: { timelineSegmentId: segment.id, op: opPrefix },
      }),
    );
    dispatch(
      createClassEvent({
        type: ClassEventTypeType.TIMELINE_SEGMENT_SECONDARY_ACTION,
        userId: currentUserId,
        roomId: activeRoomId,
        data: { "timeline-segment-id": segment.id, op: opPrefix },
      }),
    );
    // TODO: Handle logging this appropriately for analytics.
  };

  const getSecondaryActions = () => {
    if (segment.op === TimelineSegmentOpType.FeatureAllGroupsPeopleAndNotes) {
      const featuredBreakout = breakouts.find((breakout) => breakout.title === segment.breakout);
      const featuredBreakoutRooms = Object.values(breakoutRooms).filter(
        (room) => room.breakout_id === featuredBreakout?.id,
      );
      return (
        featuredBreakoutRooms.map((room) => ({
          op: `${TimelineSecondaryActionOpPrefix.FeatureGroup}:${room.id}`,
          title: `Feature Group ${room.title}`,
          icon: <UserIcon stroke="white" />,
        })) || []
      );
    }
    if (segment.op === TimelineSegmentOpType.FeatureAllPreclassGroups) {
      return (
        Object.values(preclassGroups).map((group) => ({
          op: `${TimelineSecondaryActionOpPrefix.FeaturePreclassGroup}:${group.id}`,
          title: `Feature Group ${group.title.split(" ").pop()}`,
          icon: <UserIcon stroke="white" />,
        })) || []
      );
    }
    if (segment.op === TimelineSegmentOpType.Breakout) {
      if (hasActiveBreakout) {
        return [
          {
            op: TimelineSecondaryActionOpPrefix.MessageAll,
            title: "Message All",
            icon: <ChatIcon color="white" />,
          },
          {
            op: TimelineSecondaryActionOpPrefix.EndBreakout,
            title: "End Breakout",
            icon: <XDeleteIcon stroke="white" />,
          },
        ];
      }
      return [
        {
          op: TimelineSecondaryActionOpPrefix.RestartBreakout,
          title: "Restart Breakout",
          icon: <RefreshIcon color="white" />,
        },
      ];
    }
    if (hasPollsInTimelineSegment(segment)) {
      const inProgressPollSessions = pollSessionsOnStage.filter(
        (pollSession) => pollSession.status === PollSessionStatusType.InProgress,
      );
      return [
        {
          op: TimelineSecondaryActionOpPrefix.RestartPoll,
          title: `Restart ${pluralize("Poll", pollSessionsOnStage.length)}`,
          icon: <RefreshIcon color="white" />,
        },
        {
          op: TimelineSecondaryActionOpPrefix.EndPoll,
          title: `End ${pluralize("Poll", pollSessionsOnStage.length)}`,
          icon: <XDeleteIcon stroke={inProgressPollSessions.length === 0 ? "#676767" : "white"} />,
          disabled: inProgressPollSessions.length === 0,
        },
      ];
    }
    return [];
  };

  const renderSecondaryActions = (disabled: boolean) => {
    if (!isActive) {
      return null;
    }

    const secondaryActions = getSecondaryActions();
    // We want to group the secondary actions into rows of 2.
    const secondaryActionRows = chunk(secondaryActions, 2);

    return (
      <>
        {secondaryActionRows.map((secondaryActionsInRow: TimelineSecondaryAction[]) => (
          <div key={secondaryActionsInRow[0].title} className="flex justify-start">
            {secondaryActionsInRow.map((action, rowIndex) => (
              <span
                key={action.op}
                className="flex-auto"
                style={{
                  borderTop: "1px solid var(--blue-shade-40)",
                  borderRight:
                    rowIndex === 0 && secondaryActionsInRow.length > 1
                      ? "1px solid var(--blue-shade-40)"
                      : "",
                }}
              >
                <Button
                  kind={ButtonKinds.ClassroomPrimary}
                  icon={action.icon}
                  className="m0 secondary-action"
                  onClick={() => performSecondaryAction(action.op)}
                  disabled={disabled || action.disabled}
                  key={action.title}
                  data-testid={`timeline-${action.op}`}
                >
                  {action.title}
                </Button>
              </span>
            ))}
          </div>
        ))}
        {isActive && hasInstructorNotes && (
          <Button
            kind={ButtonKinds.ClassroomPrimary}
            icon={<NotesIcon color="white" />}
            className="mt1"
            onClick={toggleShowInstructorNotes}
          >
            Instructor Notes
          </Button>
        )}
      </>
    );
  };

  const breakout = getBreakoutForTimelineSegment(segment, breakouts);
  const disabled =
    (segment.op === TimelineSegmentOpType.Breakout &&
      isBreakoutBasedOnPoll(breakout, currentlyBackingUpPoll)) ||
    (hasActiveBreakout && !isActive);

  return (
    <div
      className={clsx("mt2 timeline-segment", {
        "in-progress": isActive,
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid="timeline-segment"
      id={`timeline-segment-${segment.id}`}
    >
      <Button
        kind={ButtonKinds.ClassroomSecondary}
        className="border-none p3 cursor-pointer flex items-center left-align text-white main-action"
        onClick={() => {
          if (!hasActiveBreakout) {
            advanceTimeline(segment);
          }
        }}
        disabled={disabled}
        data-testid="timeline-segment-button"
      >
        {getIcon()}
        <span className="flex-auto truncate">{segment.title}</span>
      </Button>
      {renderSecondaryActions(disabled)}
    </div>
  );
}

export { TimelineSegment };
